import React, { Component } from 'react'
import { createComponent } from 'react-fela'
import PropTypes from 'prop-types'
import { Box } from 'kilvin'

import Icon from 'design-framework/common/ui/Icon'
import errorIcon from 'design-framework/common/icons/input-error.svg'
import CheckedIcon from 'design-framework/common/icons/check-box-active.svg'
import UncheckedIcon from 'design-framework/common/icons/check-box-inactive.svg'
import { __widget, __label, __message, __text } from './__style'

const Label = createComponent(__label, 'label', ['onClick'])
const Widget = createComponent(__widget)
const Message = createComponent(__message)
const Text = createComponent(__text)

/**
 * A checkbox that can be checked and unchecked.
 */
export default class CheckBox extends Component {
	static propTypes = {
		/** Width and height of the checkbox. */
		size: PropTypes.number,
		/** Toggle this to check and uncheck the checkbox. */
		isChecked: PropTypes.bool,
		/** Toggle this to enable and disable the checkbox. */
		isEnabled: PropTypes.bool,
		/** Checkbox is enabled but is not toggleable */
		isReadOnly: PropTypes.bool,
		/** Callback to call when checkbox is toggled. */
		onToggle: PropTypes.func,
		/** A title to identify checkbox uniquely. */
		title: PropTypes.string,
		/** A name to create checkbox object key. */
		name: PropTypes.string,
		/** Use title prop to add a text next to the checkbox */
		showTitle: PropTypes.bool,
		/** Used to remove topMargin from CheckBox */
		noMargin: PropTypes.bool,
		/** Custom Top Margin for Checkbox */
		topMargin: PropTypes.number,
		/** Disable event bubbline. Defautls to false */
		disableEventBubbling: PropTypes.bool,
		/** label styles props */
		labelStyles: PropTypes.object,
		/** Id can be used for label click on a section along with htmlFor */
		id: PropTypes.string,
	}

	static defaultProps = {
		size: 20,
		isChecked: false,
		isEnabled: true,
		isReadOnly: false,
		name: 'checkbox-option',
		disableEventBubbling: false,
		id: null,
	}

	constructor(props) {
		super(props)
		this.state = {
			isChecked: props.isChecked,
		}
	}

	toggleCheckBox = e => {
		if (this.props.disableEventBubbling) e.stopPropagation()
		if (!this.props.readOnly) {
			this.setState(
				({ isChecked }) => ({
					isChecked: !isChecked,
				}),
				() => {
					this.props.onToggle(
						this.props.title
							? {
									title: this.props.title,
									name: this.props.name,
									isChecked: this.state.isChecked,
									value: this.props.value,
							  }
							: this.state.isChecked,
						e
					)
				}
			)

			const {
				name,
				formErrors = {},
				updateErrors = () => {},
			} = this.props
			const { errorArr = [] } = formErrors

			if (errorArr.length) {
				updateErrors({
					focusOnField: false,
					errorArr: formErrors.errorArr.filter(
						item => item.fieldName !== name
					),
				})
			}
		}
	}

	// eslint-disable-next-line camelcase
	UNSAFE_componentWillReceiveProps(nextProps) {
		const { isChecked } = this.state
		if (nextProps.isChecked !== isChecked)
			this.setState({
				isChecked: nextProps.isChecked,
			})
	}

	renderInputElement = () => {
		const { isEnabled, isReadOnly, id } = this.props
		const { isChecked } = this.state
		if (!isEnabled || isReadOnly)
			return (
				<input
					type="checkbox"
					disabled={!isEnabled}
					checked={isEnabled && isChecked}
					readOnly={isEnabled && isReadOnly}
					value={isEnabled ? isChecked : ''}
				/>
			)

		return (
			<input
				type="checkbox"
				value={isChecked}
				checked={isChecked}
				onClick={this.toggleCheckBox}
				onKeyPress={this.toggleCheckBox}
				id={id}
			/>
		)
	}

	renderTitle = () => {
		const { showTitle, title, fontSize, labelStyles } = this.props
		if (showTitle && title) {
			return (
				<Label
					onClick={this.toggleCheckBox}
					fontSize={fontSize}
					style={labelStyles}
				>
					{title}
				</Label>
			)
		}

		return null
	}

	render() {
		const {
			name,
			isEnabled,
			size,
			description,
			noMargin,
			topMargin = 20,
			formErrors = {},
			updateErrors = () => {},
			style = {},
			descriptionStyles = {},
		} = this.props
		const { isChecked } = this.state

		const { errorArr = [], focusOnField = false } = formErrors
		let formSubmissionErrorText = ''

		if (errorArr.length) {
			const errorIndex = errorArr.findIndex(
				errorItem => errorItem.fieldName === name
			)

			formSubmissionErrorText = errorArr[errorIndex]
				? errorArr[errorIndex].message
				: ''

			if (errorIndex === 0 && formSubmissionErrorText && focusOnField) {
				const elem = document.querySelector(`[data-auto=field-${name}]`)
				const { y = 0 } = elem.getBoundingClientRect()
				window.scrollTo({
					top: y,
					left: 0,
					behavior: 'smooth',
				})
				updateErrors({
					focusOnField: false,
					errorArr,
				})
			}
		}

		return (
			<div
				data-auto={`field-${name}`}
				style={{ position: 'relative', ...style }}
			>
				<Box
					row
					alignItems="center"
					style={{ marginTop: noMargin ? 0 : topMargin }}
				>
					<Widget
						isEnabled={isEnabled}
						isChecked={isChecked}
						size={size}
					>
						<Icon
							glyph={
								isEnabled && isChecked
									? CheckedIcon
									: UncheckedIcon
							}
						/>

						{this.renderInputElement()}
					</Widget>
					{this.renderTitle()}
				</Box>

				{description && (
					<Message style={descriptionStyles}>{description}</Message>
				)}

				{formSubmissionErrorText && (
					<Text hasError>
						<Icon width="14" height="14" glyph={errorIcon} />
						{formSubmissionErrorText}
					</Text>
				)}
			</div>
		)
	}
}
